import { Alert, Button, Spin } from "@arco-design/web-react";
import { RefreshCw } from "lucide-react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { shareErrorMessage } from "../service/itineraryShareService";
import { useItineraryShare } from "../runtime/useItineraryShare";
import { ItineraryClientDetail } from "./ItineraryClientDetail";
import { ItineraryTableDocument } from "./ItineraryTableDocument";
import "./itineraryEditor.css";

/** 独立客户行程分享页面，先完成微信授权，再读取并记录本次打开。 */
export function ItineraryPublicShareFeaturePage({ token }: { token: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const oauthEvent = params.get("oauth_event") ?? undefined;
  const oauthError = params.get("oauth_error") ?? undefined;
  const share = useItineraryShare(token, { initialEventId: oauthEvent, initialErrorCode: oauthError });
  useEffect(() => {
    if (oauthEvent) void navigate(location.pathname, { replace: true });
  }, [location.pathname, navigate, oauthEvent]);
  useEffect(() => {
    if (share.authorizationUrl && !oauthError) window.location.assign(share.authorizationUrl);
  }, [oauthError, share.authorizationUrl]);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "花儿艺术旅行";
    document.documentElement.classList.add("itinerary-public-root");
    return () => { document.title = previousTitle; document.documentElement.classList.remove("itinerary-public-root"); };
  }, []);
  if (oauthError) return <main className="itinerary-public-state"><Alert type="error" content={shareErrorMessage(oauthError) ?? "微信授权未完成，请重新打开分享。"} /></main>;
  if (share.openQuery.isError || share.contentQuery.isError) {
    const stableShareError = share.errorCode === "share_not_found"
      || share.errorCode === "share_revoked"
      || share.errorCode === "share_expired";
    return <main className="itinerary-public-state">
      <Alert type="error" content={shareErrorMessage(share.errorCode) ?? "暂时无法加载客户行程，请稍后重试。"} />
      {!stableShareError && <Button icon={<RefreshCw size={16} />} onClick={() => void (share.contentQuery.isError ? share.contentQuery.refetch() : share.openQuery.refetch())}>重试</Button>}
    </main>;
  }
  if (share.openQuery.isPending || !share.contentQuery.data) return <main className="itinerary-public-state" role="status" aria-live="polite"><Spin size={32} /><span>{share.authorizationUrl ? "正在跳转微信授权..." : "正在验证微信授权..."}</span></main>;
  const content = share.contentQuery.data;
  return <main className="itinerary-public-page"><div id="itinerary-public-output-panel" className="itinerary-public-document" role="region" aria-label={"segments" in content ? "行程表格内容" : "行程详情内容"}>
    {"segments" in content ? <ItineraryTableDocument itinerary={content} /> : <ItineraryClientDetail itinerary={content} showDayNavigation={false} />}
  </div></main>;
}
